import { useState } from "react";
import { mt, MBtn, MCard, elapsedMin } from "./shared";

// Every unit status reachable from a phone (§86). Safety-critical ones confirm first.
const STATUSES = [
  ["acknowledged", "ACKNOWLEDGE", mt.cyan], ["enroute", "EN ROUTE", mt.blue],
  ["onscene", "ON SCENE", mt.green], ["staged", "STAGED", mt.purple],
  ["transporting", "TRANSPORTING", mt.purple], ["at_destination", "AT DESTINATION", mt.cyan],
  ["report_writing", "REPORT WRITING", mt.dim], ["busy", "BUSY", mt.amber],
  ["meal", "MEAL", mt.dim], ["court", "COURT", mt.dim], ["training", "TRAINING", mt.dim],
  ["out_of_service", "OUT OF SERVICE", mt.red], ["emergency_traffic", "EMERGENCY TRAFFIC", mt.red],
  ["available", "AVAILABLE", mt.green], ["clear", "CLEAR CALL", mt.green],
];
const CONFIRM_NEEDED = new Set(["out_of_service", "emergency_traffic", "clear"]);
const REASON_NEEDED = new Set(["out_of_service"]);
const DEST_NEEDED = new Set(["transporting"]);

function StatusSheet({ open, onClose, onPick, current }) {
  const [confirming, setConfirming] = useState(null);
  const [extra, setExtra] = useState("");
  if (!open) return null;
  const pick = (s, label) => {
    if (CONFIRM_NEEDED.has(s) || REASON_NEEDED.has(s) || DEST_NEEDED.has(s)) {
      setConfirming([s, label]); setExtra("");
    } else { onPick(s, {}); onClose(); }
  };
  return (
    <div data-testid="mobile-status-sheet" className="fixed inset-0 z-[850] flex flex-col justify-end" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className="rounded-t-2xl p-4 pb-8 max-h-[80vh] overflow-y-auto" style={{ background: mt.bg, border: `1px solid ${mt.border}`, borderBottom: "none" }}>
        {!confirming ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <span className="font-head font-bold text-sm" style={{ color: mt.text }}>SET UNIT STATUS (now: {(current || "—").toUpperCase()})</span>
              <MBtn small outline color={mt.dim} onClick={onClose} testid="mobile-status-close">CLOSE</MBtn>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map(([s, label, color]) => (
                <MBtn key={s} full outline={!CONFIRM_NEEDED.has(s)} color={color}
                  onClick={() => pick(s, label)} testid={`mobile-status-${s}`}>{label}</MBtn>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="font-head font-bold text-sm mb-2" style={{ color: mt.amber }}>
              CONFIRM: {confirming[1]}
            </div>
            {(REASON_NEEDED.has(confirming[0]) || DEST_NEEDED.has(confirming[0])) && (
              <input data-testid="mobile-status-extra" value={extra} onChange={(e) => setExtra(e.target.value)}
                placeholder={REASON_NEEDED.has(confirming[0]) ? "Reason (required)" : "Destination (required)"}
                className="w-full mb-3 px-3 py-3 rounded-lg font-mono text-sm"
                style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
            )}
            <div className="grid grid-cols-2 gap-2">
              <MBtn full color={mt.green} testid="mobile-status-confirm"
                disabled={(REASON_NEEDED.has(confirming[0]) || DEST_NEEDED.has(confirming[0])) && !extra.trim()}
                onClick={() => {
                  const body = REASON_NEEDED.has(confirming[0]) ? { reason: extra.trim() }
                    : DEST_NEEDED.has(confirming[0]) ? { destination: extra.trim() } : {};
                  onPick(confirming[0], body); setConfirming(null); onClose();
                }}>CONFIRM</MBtn>
              <MBtn full outline color={mt.dim} onClick={() => setConfirming(null)} testid="mobile-status-cancel">BACK</MBtn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function IncidentCard({ inc, myUnit, onStatus, onBackup, onOpenSheet }) {
  const mine = (inc.assigned_units || []).includes(myUnit);
  const loc = inc.location || {};
  return (
    <MCard testid={`mobile-call-${inc.cc_number}`}
      style={{ border: `1.5px solid ${inc.priority === 1 ? mt.red : mine ? mt.blue : mt.border}` }}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-head font-bold text-sm" style={{ color: inc.priority === 1 ? "#FCA5A5" : mt.text }}>
          P{inc.priority} · {inc.call_type_label}
        </span>
        <span className="text-[10px] font-mono" style={{ color: mt.dim }}>{elapsedMin(inc.created_at)}m</span>
      </div>
      <div className="text-xs font-mono" style={{ color: mt.dim }}>
        {inc.cc_number} · {(inc.discipline || "").toUpperCase()} · {(inc.status || "").toUpperCase()}
      </div>
      <div className="text-xs font-mono mt-0.5" style={{ color: mt.text }}>
        {loc.address}{loc.apt ? ` #${loc.apt}` : ""}
      </div>
      {inc.premise_summary && (
        <div className="text-[11px] font-mono mt-1.5 py-1 px-2 rounded" style={{ color: "#FCA5A5", background: "#3b0d0d" }}>
          {inc.premise_summary}
        </div>
      )}
      {(inc.assigned_units || []).length > 0 && (
        <div className="text-[10px] font-mono mt-1" style={{ color: mt.cyan }}>
          UNITS: {(inc.assigned_units || []).join(", ")}
        </div>
      )}
      {mine && (
        <div className="grid grid-cols-4 gap-1.5 mt-2.5">
          <MBtn small color={mt.cyan} onClick={() => onStatus("acknowledged", {}, inc.cc_number)} testid={`mobile-call-ack-${inc.cc_number}`}>ACK</MBtn>
          <MBtn small color={mt.blue} onClick={() => onStatus("enroute", {}, inc.cc_number)} testid={`mobile-call-enr-${inc.cc_number}`}>ENR</MBtn>
          <MBtn small color={mt.green} onClick={() => onStatus("onscene", {}, inc.cc_number)} testid={`mobile-call-ons-${inc.cc_number}`}>ON SCN</MBtn>
          <MBtn small outline color={mt.red} onClick={() => onBackup(inc.cc_number)} testid={`mobile-call-backup-${inc.cc_number}`}>BACKUP</MBtn>
        </div>
      )}
      {mine && (
        <div className="mt-1.5">
          <MBtn small full outline color={mt.dim} onClick={onOpenSheet} testid={`mobile-call-more-status-${inc.cc_number}`}>ALL STATUSES…</MBtn>
        </div>
      )}
    </MCard>
  );
}

export function MobileCalls({ incidents, myUnit, unitDoc, onStatus, onBackup }) {
  const [sheet, setSheet] = useState(false);
  const mineFirst = [...incidents].sort((a, b) =>
    ((b.assigned_units || []).includes(myUnit) ? 1 : 0) - ((a.assigned_units || []).includes(myUnit) ? 1 : 0)
    || (a.priority - b.priority));
  return (
    <div data-testid="mobile-calls" className="flex flex-col gap-3 p-3 pb-28">
      <div className="flex items-center justify-between">
        <span className="font-head font-bold text-sm" style={{ color: mt.text }}>ACTIVE CALLS ({incidents.length})</span>
        {myUnit && <MBtn small outline color={mt.amber} onClick={() => setSheet(true)} testid="mobile-calls-status-btn">MY STATUS</MBtn>}
      </div>
      {incidents.length === 0 && (
        <MCard><span className="text-sm font-mono" style={{ color: mt.dim }}>No active incidents.</span></MCard>
      )}
      {mineFirst.map((inc) => (
        <IncidentCard key={inc.cc_number} inc={inc} myUnit={myUnit}
          onStatus={onStatus} onBackup={onBackup} onOpenSheet={() => setSheet(true)} />
      ))}
      <StatusSheet open={sheet} onClose={() => setSheet(false)} current={unitDoc?.status}
        onPick={(s, extra) => onStatus(s, extra, unitDoc?.current_incident)} />
    </div>
  );
}
